import * as React from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

/* ──────────────────────────────────────────────────────────────────────────
   AppDatePicker — the single, premium, brand-consistent date picker for the
   whole StepUp frontend. Replaces every `<input type="date">` / native MUI
   `<TextField type="date">`.

   Contract:
     • VALUE IS ALWAYS A DATE-ONLY STRING "YYYY-MM-DD" (or "" when empty).
       The component never emits a locale-formatted or UTC datetime string,
       so the exact value the backend expects flows straight through.
     • Date-only values are treated as LOCAL calendar dates — dayjs parses
       "2026-07-17" at local midnight, so it can never shift to Jul 16/18.
     • Display format defaults to "MMM D, YYYY" (e.g. "Jul 17, 2026"); pass
       format="MM/DD/YYYY" for the compact variant.

   Built on MUI X DatePicker, so keyboard navigation, ARIA, Escape-to-close,
   focus handling, month/year navigation and the mobile modal are all handled
   by the library — we only standardise styling, I/O and the extra affordances
   (Today shortcut, clear button, min/max, disabled dates, range linkage).
   ────────────────────────────────────────────────────────────────────────── */

export const DATE_ONLY = "YYYY-MM-DD";
export const DEFAULT_DISPLAY_FORMAT = "MMM D, YYYY";

/** Parse a date-only string into a LOCAL Dayjs, or null. No timezone shift. */
export function parseDateOnly(value?: string | null): Dayjs | null {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
}

/** Serialise a Dayjs back to a date-only "YYYY-MM-DD" string, or "" when null. */
export function serializeDateOnly(d: Dayjs | null | undefined): string {
  return d && d.isValid() ? d.format(DATE_ONLY) : "";
}

export interface AppDatePickerProps {
  /** Date-only "YYYY-MM-DD" (or "" / null). */
  value?: string | null;
  /** Emits a date-only "YYYY-MM-DD" string, or "" when cleared. */
  onChange: (value: string) => void;
  label?: React.ReactNode;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  /** Inclusive minimum selectable date, "YYYY-MM-DD". */
  minDate?: string | null;
  /** Inclusive maximum selectable date, "YYYY-MM-DD". */
  maxDate?: string | null;
  disablePast?: boolean;
  disableFuture?: boolean;
  /** Return true to disable a specific date; receives the date-only string. */
  shouldDisableDate?: (dateOnly: string) => boolean;
  /** Show the inline clear (×) button + Clear action. Default true. */
  clearable?: boolean;
  fullWidth?: boolean;
  size?: "small" | "medium";
  /** Display format, default "MMM D, YYYY". */
  format?: string;
  name?: string;
  autoFocus?: boolean;
  /** Accessible label when no visible `label` is passed. */
  ariaLabel?: string;
  sx?: any;
  /** Escape hatch for extra MUI X slotProps (merged last). */
  slotProps?: any;
}

const AppDatePicker: React.FC<AppDatePickerProps> = ({
  value,
  onChange,
  label,
  placeholder,
  required = false,
  disabled = false,
  error = false,
  helperText,
  minDate,
  maxDate,
  disablePast = false,
  disableFuture = false,
  shouldDisableDate,
  clearable = true,
  fullWidth = true,
  size = "small",
  format = DEFAULT_DISPLAY_FORMAT,
  name,
  autoFocus = false,
  ariaLabel,
  sx,
  slotProps,
}) => {
  const parsed = parseDateOnly(value);
  const min = parseDateOnly(minDate) ?? undefined;
  const max = parseDateOnly(maxDate) ?? undefined;

  const resolvedAriaLabel =
    ariaLabel || (typeof label === "string" ? label : undefined);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label={label}
        value={parsed}
        format={format}
        disabled={disabled}
        minDate={min}
        maxDate={max}
        disablePast={disablePast}
        disableFuture={disableFuture}
        autoFocus={autoFocus}
        shouldDisableDate={
          shouldDisableDate
            ? (d: Dayjs) => shouldDisableDate(serializeDateOnly(d))
            : undefined
        }
        onChange={(d: Dayjs | null) => onChange(serializeDateOnly(d))}
        slotProps={{
          textField: {
            size,
            fullWidth,
            required,
            error,
            helperText,
            name,
            placeholder,
            inputProps: resolvedAriaLabel ? { "aria-label": resolvedAriaLabel } : undefined,
            sx: {
              // Premium field surface — subtle border, brand-tinted focus
              // ring, dark-navy date text. All colors resolve from CSS
              // variables so white-label tenants pick up their own brand
              // automatically (with neutral fallbacks when unset).
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                bgcolor: "#fff",
                transition: "box-shadow 160ms ease, border-color 160ms ease",
                "& fieldset": { borderColor: "rgba(15,23,42,0.14)" },
                "&:hover fieldset": { borderColor: "rgba(15,23,42,0.28)" },
                "&.Mui-focused fieldset": {
                  borderColor: "var(--brand-primary, #4f46e5)",
                  borderWidth: "1px",
                },
                "&.Mui-focused": {
                  boxShadow: "0 0 0 3px rgba(79,70,229,0.15)",
                },
              },
              "& .MuiOutlinedInput-input": {
                color: "var(--brand-accent, #10233F)",
                fontWeight: 600,
                letterSpacing: 0.1,
              },
              ...(sx || {}),
            },
          },
          // Root cause of the invisible calendar icon: MUI X's default
          // `openPickerButton` inherits the ambient text color, which on
          // the Today page's dark-hero background over a translucent
          // input rendered near-white on white. Anchor it explicitly to
          // the brand accent (dark navy) with a soft branded hover so
          // it reads as an obvious affordance everywhere the picker is
          // used, regardless of the surrounding surface.
          openPickerButton: {
            "aria-label": resolvedAriaLabel ? `${resolvedAriaLabel} — open calendar` : "Open calendar",
            sx: {
              color: "var(--brand-accent, #10233F)",
              borderRadius: "8px",
              width: 36,
              height: 36,
              mr: -0.5,
              transition: "background-color 140ms ease, color 140ms ease",
              "&:hover": {
                bgcolor: "rgba(16,35,63,0.08)",
                color: "var(--brand-primary, #4f46e5)",
              },
              "&.Mui-disabled": { color: "rgba(15,23,42,0.28)" },
            },
          },
          openPickerIcon: {
            sx: { fontSize: 20 },
          },
          // Inline clear (×) when the field is optional.
          field: { clearable },
          // Today shortcut + Clear (mobile modal / action bar).
          actionBar: {
            actions: clearable ? ["clear", "today"] : ["today", "accept"],
            sx: {
              px: 1.25, py: 1,
              "& .MuiButton-root": {
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 700,
                fontSize: 12.5,
              },
              // The final button (Accept/Today) is the primary action; MUI
              // renders it last. Style it as the brand primary CTA.
              "& .MuiButton-root:last-of-type": {
                bgcolor: "var(--brand-primary, #4f46e5)",
                color: "#fff",
                px: 1.75,
                "&:hover": {
                  bgcolor: "var(--brand-accent, #10233F)",
                },
              },
            },
          },
          // Popper — pin to input, escape parent overflow-clipped cards, and
          // reposition on scroll/resize so the calendar never floats away
          // from its anchor. `strategy: fixed` lifts the popup out of the
          // scroll container so it stays anchored to the field even inside
          // billing/setting cards that use `overflow: hidden`.
          popper: {
            placement: "bottom-start",
            strategy: "fixed",
            modifiers: [
              { name: "offset", options: { offset: [0, 6] } },
              { name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
              { name: "flip", options: { fallbackPlacements: ["top-start", "bottom-end", "top-end"] } },
              { name: "eventListeners", options: { scroll: true, resize: true } },
            ],
            sx: { zIndex: (theme: any) => theme.zIndex.modal + 1 },
          },
          desktopPaper: {
            sx: {
              borderRadius: "14px",
              boxShadow: "0 20px 48px rgba(15,23,42,0.18)",
              border: "1px solid rgba(15,23,42,0.08)",
              overflow: "hidden",
              // ── Premium calendar body ────────────────────────────
              // All selectors below target STABLE MUI X class names
              // (documented API), not the emotion-generated hashes.
              // Colors flow from --brand-* CSS variables so the
              // white-label tenants pick up their own brand.
              "& .MuiPickersCalendarHeader-root": {
                pl: 2, pr: 1, pt: 1.25, pb: 0.5,
                mt: 0,
              },
              "& .MuiPickersCalendarHeader-label": {
                fontSize: 15, fontWeight: 800,
                color: "var(--brand-accent, #10233F)",
                letterSpacing: 0.2,
              },
              "& .MuiPickersCalendarHeader-switchViewButton": {
                color: "var(--brand-accent, #10233F)",
                "&:hover": { bgcolor: "rgba(16,35,63,0.06)" },
              },
              "& .MuiPickersArrowSwitcher-button": {
                color: "var(--brand-accent, #10233F)",
                borderRadius: "8px",
                "&:hover": { bgcolor: "rgba(16,35,63,0.08)" },
                "&.Mui-disabled": { color: "rgba(15,23,42,0.22)" },
              },
              "& .MuiDayCalendar-weekDayLabel": {
                fontSize: 11, fontWeight: 700,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: 0.5,
                width: 36, height: 32,
              },
              "& .MuiPickersDay-root": {
                fontSize: 13, fontWeight: 600,
                width: 36, height: 36,
                color: "var(--brand-accent, #10233F)",
                borderRadius: "10px",
                margin: "1px",
                transition: "background-color 120ms ease, color 120ms ease",
                "&:hover": {
                  bgcolor: "rgba(79,70,229,0.10)",
                },
                "&:focus": {
                  bgcolor: "rgba(79,70,229,0.14)",
                },
              },
              // Today (not selected) — subtle ring only, so the
              // selection state remains the clearly dominant one.
              "& .MuiPickersDay-today:not(.Mui-selected)": {
                border: "1.5px solid var(--brand-primary, #4f46e5)",
                color: "var(--brand-primary, #4f46e5)",
                background: "transparent",
              },
              // Selected day — brand primary fill, white text.
              "& .MuiPickersDay-root.Mui-selected": {
                bgcolor: "var(--brand-primary, #4f46e5) !important",
                color: "#fff !important",
                fontWeight: 800,
                boxShadow: "0 4px 10px rgba(79,70,229,0.25)",
                "&:hover": { bgcolor: "var(--brand-accent, #10233F) !important" },
                "&:focus": { bgcolor: "var(--brand-accent, #10233F) !important" },
              },
              // Outside-current-month days — muted but readable.
              "& .MuiPickersDay-dayOutsideMonth": {
                color: "rgba(15,23,42,0.32)",
              },
              // Disabled days — dim but not invisible.
              "& .MuiPickersDay-root.Mui-disabled": {
                color: "rgba(15,23,42,0.28)",
              },
              "& .MuiYearCalendar-root .MuiPickersYear-yearButton.Mui-selected, & .MuiMonthCalendar-root .MuiPickersMonth-monthButton.Mui-selected": {
                bgcolor: "var(--brand-primary, #4f46e5)",
                color: "#fff",
                fontWeight: 800,
                "&:hover": { bgcolor: "var(--brand-accent, #10233F)" },
              },
            },
          },
          ...(slotProps || {}),
        }}
      />
    </LocalizationProvider>
  );
};

export default AppDatePicker;
